import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Alert, Typography } from 'antd';
import {
    ShopOutlined,
    ShoppingCartOutlined,
    DollarOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const { Title } = Typography;

const PharmacyDashboard = () => {
    const { t } = useTranslation(['pharmacy', 'common']);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({
        totalItems: 0,
        lowStockItems: 0,
        expiringItems: 0,
        todaySales: 0,
        revenueIQD: 0,
        revenueUSD: 0,
        pendingIQD: 0,
        pendingUSD: 0
    });

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            // Fetch inventory stats
            const inventoryRes = await api.get('/pharmacy/reports/inventory');
            const inventoryData = inventoryRes.data?.data || {};

            // Fetch today's sales
            const today = new Date().toISOString().split('T')[0];
            const salesRes = await api.get(`/pharmacy/reports/sales?startDate=${today}&endDate=${today}`);
            const salesData = salesRes.data?.data || {};

            // Calculate revenue by currency
            const salesList = salesData.sales || [];
            const revenueIQD = salesList.filter(s => s.currency === 'IQD').reduce((sum, s) => sum + s.totalAmount, 0);
            const revenueUSD = salesList.filter(s => s.currency === 'USD').reduce((sum, s) => sum + s.totalAmount, 0);
            const pendingIQD = salesList.filter(s => s.currency === 'IQD').reduce((sum, s) => sum + (s.balanceDue || 0), 0);
            const pendingUSD = salesList.filter(s => s.currency === 'USD').reduce((sum, s) => sum + (s.balanceDue || 0), 0);

            setStats({
                totalItems: inventoryData.totalItems || 0,
                lowStockItems: inventoryData.lowStockItems || 0,
                expiringItems: inventoryData.expiringItems || 0,
                todaySales: salesData.totalSales || 0,
                revenueIQD,
                revenueUSD,
                pendingIQD,
                pendingUSD
            });

            setError('');
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err.response?.data?.message || t('pharmacy:messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 100 }}>
                <Spin size="large" tip={t('loading')} />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>
                {t('pharmacy:dashboard.title')}
            </Title>

            {error && (
                <Alert
                    message={t('pharmacy:dashboard.error')}
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:dashboard.totalItems')}
                            value={stats.totalItems}
                            prefix={<ShopOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:dashboard.lowStockItems')}
                            value={stats.lowStockItems}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:dashboard.expiringSoon')}
                            value={stats.expiringItems}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:dashboard.todaysSales')}
                            value={stats.todaySales}
                            prefix={<ShoppingCartOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:dashboard.todaysRevenue')}
                            value={stats.revenueUSD}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                            precision={2}
                            formatter={(value) => `$${Number(value).toFixed(2)}`}
                            suffix={<span style={{ fontSize: 14, color: '#999' }}>{stats.revenueIQD > 0 ? ` | IQD ${stats.revenueIQD.toLocaleString()}` : ''}</span>}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('pharmacy:dashboard.pendingPayments')}
                            value={stats.pendingUSD}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#13c2c2' }}
                            precision={2}
                            formatter={(value) => `$${Number(value).toFixed(2)}`}
                            suffix={<span style={{ fontSize: 14, color: '#999' }}>{stats.pendingIQD > 0 ? ` | IQD ${stats.pendingIQD.toLocaleString()}` : ''}</span>}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PharmacyDashboard;
